import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { createUser } from '../../services/Users/userService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import TimeInput from '../Utils/TimeInput';

const CreateUserModal = ({ show, handleClose, refreshUsers }) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (!show) {
      reset();
      setActiveTab('general');
    }
  }, [show, reset]);

  const accessStartTime = watch('accessStartTime');
  const accessEndTime = watch('accessEndTime');

  const validateTimeRange = (value, fieldName) => {
    if (!value) {
      if (fieldName === 'accessStartTime' && accessEndTime) {
        return 'Início do Acesso é obrigatório quando Final do Acesso está preenchido';
      }
      if (fieldName === 'accessEndTime' && accessStartTime) {
        return 'Final do Acesso é obrigatório quando Início do Acesso está preenchido';
      }
      return true;
    }

    if (!/^\d{2}:\d{2}:\d{2}$/.test(value)) {
      return true;
    }

    if (fieldName === 'accessStartTime' && accessEndTime && /^\d{2}:\d{2}:\d{2}$/.test(accessEndTime)) {
      const start = value.split(':').map(Number);
      const end = accessEndTime.split(':').map(Number);
      const startSeconds = start[0] * 3600 + start[1] * 60 + start[2];
      const endSeconds = end[0] * 3600 + end[1] * 60 + end[2];
      
      if (startSeconds >= endSeconds) {
        return 'Início do Acesso deve ser antes do Final do Acesso';
      }
    }

    if (fieldName === 'accessEndTime' && accessStartTime && /^\d{2}:\d{2}:\d{2}$/.test(accessStartTime)) {
      const start = accessStartTime.split(':').map(Number);
      const end = value.split(':').map(Number);
      const startSeconds = start[0] * 3600 + start[1] * 60 + start[2];
      const endSeconds = end[0] * 3600 + end[1] * 60 + end[2];
      
      if (startSeconds >= endSeconds) {
        return 'Final do Acesso deve ser depois do Início do Acesso';
      }
    }

    return true;
  };

  const onSubmit = async (data) => {
    const payload = {
      fullname: data.fullname,
      username: data.username,
      password: data.password,
      role: data.role,
    };

    if (data.accessStartTime && data.accessEndTime) {
      payload.accessStartTime = data.accessStartTime;
      payload.accessEndTime = data.accessEndTime;
    }

    try {
      await createUser(payload);
      setSuccessMessage('Usuário criado com sucesso!');
      setShowSuccessModal(true);
      reset();
      setActiveTab('general');
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao criar usuário.');
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshUsers();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Usuário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            <Tab eventKey="general" title="Dados Gerais">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group controlId="formFullName" className="mb-3">
                  <Form.Label className="mb-1">Nome Completo</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('fullname', { required: 'Nome completo é obrigatório' })}
                    isInvalid={!!errors.fullname}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.fullname && errors.fullname.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="formUsername" className="mb-3">
                  <Form.Label className="mb-1">Usuário</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('username', { required: 'Usuário é obrigatório' })}
                    isInvalid={!!errors.username}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username && errors.username.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="formPassword" className="mb-3">
                  <Form.Label className="mb-1">Senha</Form.Label>
                  <Form.Control
                    type="password"
                    {...register('password', { required: 'Senha é obrigatória' })}
                    isInvalid={!!errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password && errors.password.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="formRole" className="mb-3">
                  <Form.Label className="mb-1">Permissão</Form.Label>
                  <Form.Select {...register('role', { required: 'Permissão é obrigatória' })}>
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                  </Form.Select>
                </Form.Group>

                <TimeInput
                  label="Início do Acesso"
                  name="accessStartTime"
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  rules={{
                    validate: (value) => validateTimeRange(value, 'accessStartTime'),
                  }}
                />

                <TimeInput
                  label="Final do Acesso"
                  name="accessEndTime"
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  rules={{
                    validate: (value) => validateTimeRange(value, 'accessEndTime'),
                  }}
                />

                <Button variant="primary" className="button-custom w-100" type="submit">
                  Cadastrar
                </Button>
              </Form>
            </Tab>
            <Tab eventKey="permissions" title="Permissões">
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreateUserModal;